import React, { useEffect, useRef } from "react";
import { useRawJwt } from "~/Auth/AuthenticationManager";
import { apiFetch, HttpError } from "~/api-fetch/apiFetch";
import { reportError } from "~/error-handler/reportError";
import {
  FuelExpense,
  ReceiptValuedExpense,
  PerDiemExpense,
  AdHocExpense,
} from "~/gql/types";

type UseLoadReceiptProps = {
  image: any;
  expenseEntry:
    | ReceiptValuedExpense
    | FuelExpense
    | PerDiemExpense
    | AdHocExpense;
  setImage: React.Dispatch<any>;
};

type LoadReceiptImageArgs = {
  url: string;
  token: string | null;
  setImage: React.Dispatch<any>;
  report?: (error: HttpError) => void;
};

export async function loadReceiptImage({
  url,
  token,
  setImage,
  report = reportError,
}: LoadReceiptImageArgs): Promise<void> {
  const res = await apiFetch(
    url.startsWith("/") ? url : `/${url}`,
    {
      method: "get",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    },
    { operation: "loadReceiptImage" }
  );

  if (!res.ok) {
    // Classified for telemetry only; the response is still handled below
    // exactly as before.
    report(HttpError.fromResponse(res));
  }

  if (res && res.headers.get("Content-Type")) {
    const type = res.headers.get("Content-Type");
    const blob = await res.blob();
    const file = new Blob([blob], { type: type as string });
    if (setImage) {
      setImage(file);
    }
  }
}

export function useLoadReceipt({
  image,
  expenseEntry,
  setImage,
}: UseLoadReceiptProps): void {
  const token = useRawJwt();
  // The receipt is reloaded whenever the entry is refetched or the token
  // renews, so one that keeps failing would otherwise be reported every time.
  const reportedUrls = useRef(new Set<string>());

  useEffect(() => {
    const url = expenseEntry.receiptImageUrl;
    if (!url) {
      return;
    }

    if (!image) {
      // Not awaited: a network failure surfaces as an unhandled rejection,
      // which the global error listener classifies and notifies the user about.
      void loadReceiptImage({
        url,
        token,
        setImage,
        report: (error) => {
          if (!reportedUrls.current.has(url)) {
            reportedUrls.current.add(url);
            reportError(error);
          }
        },
      });
    }
  }, [expenseEntry, image, token]);
}
